import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, RefreshCw, UserRound } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { IPropertyMember } from '@/domain/entities/property-member';
import { PropertyMemberStatus } from '@/domain/entities/property-member';
import {
  fetchPropertyMemberProfile,
  fetchPropertyMembers,
  selectHasFetchedPropertyMembersByPropertyID,
  selectPropertyMembersByPropertyID,
  selectPropertyMembersErrorByPropertyID,
  selectPropertyMembersLoadingByPropertyID,
} from '@/features/property-members';
import { fetchOrganization } from '@/features/organizations/organizationsThunks';
import { formatUnixDateTimeCompact } from '@/lib';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

type MembersTabProps = {
  propertyId: string;
};

type MemberTarget =
  | {
      kind: 'organization';
      id: string;
    }
  | {
      kind: 'homeowner';
      id: string;
    }
  | {
      kind: 'business';
      id: string;
    }
  | {
      kind: 'unknown';
      id: string;
    };

function labelFromValue(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'outline' {
  if (status === PropertyMemberStatus.Active) return 'default';
  if (status === PropertyMemberStatus.Invited) return 'secondary';
  return 'outline';
}

function getMemberTarget(member: IPropertyMember): MemberTarget | null {
  const organizationId = member.orgID?.trim() || member.organizationID?.trim();
  if (
    organizationId ||
    member.memberType === 'ORGANIZATION' ||
    member.memberType === 'organization' ||
    member.memberType === 'org'
  ) {
    const id = organizationId || member.memberID?.trim();
    return id ? { kind: 'organization', id } : null;
  }

  const userId = member.userID?.trim() || member.memberID?.trim();
  if (!userId) return null;
  if (member.profileType === 'homeowner') return { kind: 'homeowner', id: userId };
  if (member.profileType === 'business') return { kind: 'business', id: userId };
  return { kind: 'unknown', id: userId };
}

function getMemberDisplayName(
  profile?: { firstName?: string; lastName?: string; email?: string } | null,
): string | null {
  if (!profile) return null;
  const fullName = `${profile.firstName?.trim() ?? ''} ${profile.lastName?.trim() ?? ''}`.trim();
  return fullName || profile.email?.trim() || null;
}

function formatTimestamp(value?: number): string {
  return value ? formatUnixDateTimeCompact(value) : 'N/A';
}

export function MembersTab({ propertyId }: MembersTabProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const members = useAppSelector(selectPropertyMembersByPropertyID(propertyId));
  const isLoading = useAppSelector(selectPropertyMembersLoadingByPropertyID(propertyId));
  const hasFetched = useAppSelector(selectHasFetchedPropertyMembersByPropertyID(propertyId));
  const error = useAppSelector(selectPropertyMembersErrorByPropertyID(propertyId));
  const profilesByUserID = useAppSelector((state) => state.propertyMembers.profilesByUserID ?? {});
  const organizations = useAppSelector((state) => state.organizations.items);

  const organizationsById = useMemo(
    () => new Map(organizations.map((organization) => [organization.id, organization])),
    [organizations],
  );

  useEffect(() => {
    if (!propertyId) return;
    dispatch(fetchPropertyMembers({ propertyID: propertyId }));
  }, [dispatch, propertyId]);

  useEffect(() => {
    const queuedProfileIds = new Set<string>();
    const queuedOrganizationIds = new Set<string>();

    for (const member of members) {
      const target = getMemberTarget(member);
      if (!target) continue;

      if (target.kind === 'organization') {
        if (!organizationsById.has(target.id) && !queuedOrganizationIds.has(target.id)) {
          queuedOrganizationIds.add(target.id);
          dispatch(fetchOrganization({ orgId: target.id }));
        }
        continue;
      }

      if (
        (target.kind === 'homeowner' || target.kind === 'business') &&
        !profilesByUserID[target.id] &&
        !queuedProfileIds.has(target.id)
      ) {
        queuedProfileIds.add(target.id);
        dispatch(fetchPropertyMemberProfile({ userID: target.id, profileType: target.kind }));
      }
    }
  }, [dispatch, members, organizationsById, profilesByUserID]);

  const refreshMembers = () => {
    dispatch(fetchPropertyMembers({ propertyID: propertyId, force: true }));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>Members</CardTitle>
          <p className="text-muted-foreground mt-1 text-sm">People and organizations associated with this property.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={refreshMembers} disabled={isLoading}>
          <RefreshCw className={isLoading ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive mb-4 rounded-md border p-3 text-sm">
            {error}
          </div>
        ) : null}

        {!hasFetched && isLoading ? <p className="text-muted-foreground text-sm">Loading members...</p> : null}

        {hasFetched && members.length === 0 && !error ? (
          <p className="text-muted-foreground text-sm">No members are associated with this property.</p>
        ) : null}

        {members.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => {
                const target = getMemberTarget(member);
                const organizationProfile = target?.kind === 'organization' ? organizationsById.get(target.id) : null;
                const memberProfile =
                  target?.kind === 'homeowner' || target?.kind === 'business' ? profilesByUserID[target.id] : null;
                const displayName =
                  organizationProfile?.name || getMemberDisplayName(memberProfile) || target?.id || 'Unknown member';
                const isNavigable = Boolean(target?.id && target.kind !== 'unknown');

                return (
                  <TableRow
                    key={member.id}
                    role={isNavigable ? 'button' : undefined}
                    tabIndex={isNavigable ? 0 : undefined}
                    className={isNavigable ? 'cursor-pointer' : undefined}
                    onClick={() => {
                      if (!target) return;
                      if (target.kind === 'organization') {
                        navigate(`/admin/organizations/${encodeURIComponent(target.id)}`, {
                          state: { fromPropertyMembers: true, propertyId },
                        });
                        return;
                      }

                      if (target.kind === 'business') {
                        navigate(`/admin/users?userId=${encodeURIComponent(target.id)}`, {
                          state: { fromPropertyMembers: true, propertyId },
                        });
                        return;
                      }
                      if (target.kind !== 'homeowner') return;

                      navigate(`/admin/homeowners/${encodeURIComponent(target.id)}`, {
                        state: { fromPropertyMembers: true, propertyId },
                      });
                    }}
                    onKeyDown={(event) => {
                      if (!isNavigable || (event.key !== 'Enter' && event.key !== ' ')) return;
                      event.preventDefault();
                      event.currentTarget.click();
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {target?.kind === 'organization' ? (
                          <Building2 className="text-muted-foreground h-4 w-4" />
                        ) : (
                          <UserRound className="text-muted-foreground h-4 w-4" />
                        )}
                        <div>
                          <p className="font-medium">{displayName}</p>
                          <p className="text-muted-foreground font-mono text-xs">{target?.id ?? member.id}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {target?.kind === 'organization'
                        ? 'Organization'
                        : target?.kind === 'business'
                          ? 'Business / system user'
                          : target?.kind === 'homeowner'
                            ? 'Home owner'
                            : 'User'}
                    </TableCell>
                    <TableCell>{labelFromValue(member.role)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(member.status)}>{labelFromValue(member.status)}</Badge>
                    </TableCell>
                    <TableCell>{formatTimestamp(member.updatedAt || member.createdAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
